import { useCallback, useEffect, useMemo, useState } from 'react';

const EDITABLE = ['product_name', 'article_name', 'commercial_name', 'ingredients', 'allergens', 'storage', 'quantity'];
export const EDITABLE_LABEL_FIELDS = {
  product_name: 'Nome prodotto', article_name: 'Nome ingrediente', commercial_name: 'Nome commerciale',
  ingredients: 'Ingredienti', allergens: 'Allergeni', storage: 'Conservazione', quantity: 'Quantità indicata',
};

export default function useLabelVersions({ backendUrl, lotId, templateId, context, layout }) {
  const [versions, setVersions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sourceChanged, setSourceChanged] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    setVersions([]); setSelectedId(null); setSourceChanged(false); setEditing(false); setError('');
    if (!lotId || !templateId || !context) return undefined;
    let active = true;
    setLoading(true);
    const query = new URLSearchParams({ lot_id: lotId, template_id: templateId });
    fetch(`${backendUrl}/api/labels/versions?${query}`, { credentials: 'include' })
      .then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Impossibile caricare le etichette salvate'); return r.json(); })
      .then(result => { if (active) { setVersions(result.versions || []); setSelectedId(result.versions?.[0]?.version_id || null); setSourceChanged(!!result.source_changed); } })
      .catch(e => { if (active) setError(e.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [backendUrl, lotId, templateId, context]);

  const selected = versions.find(v => v.version_id === selectedId) || null;
  const baseData = selected?.context || context;
  const data = editing ? { ...baseData, ...draft } : baseData;
  const activeLayout = selected?.layout || layout;
  const editableFields = useMemo(() => [...new Set((activeLayout?.elements || [])
    .filter(e => e.type === 'dynamic' && EDITABLE.includes(e.field)).map(e => e.field))], [activeLayout]);

  const beginEdit = () => {
    setDraft(Object.fromEntries(editableFields.map(field => [field, data?.[field] || ''])));
    setEditing(true);
  };

  const save = useCallback(async overrides => {
    const response = await fetch(`${backendUrl}/api/labels/versions`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lot_id: lotId, template_id: templateId, base_version_id: selectedId, overrides }),
    });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).detail || 'Impossibile salvare l’etichetta');
    const version = await response.json();
    setVersions(previous => [version, ...previous]);
    setSelectedId(version.version_id); setEditing(false); setSourceChanged(previous => selectedId ? previous : false);
    return version;
  }, [backendUrl, lotId, templateId, selectedId]);

  const saveDraft = () => {
    const overrides = Object.fromEntries(editableFields
      .filter(field => draft[field] !== (context?.[field] || ''))
      .map(field => [field, draft[field]]));
    return save(overrides);
  };

  const ensureVersion = async () => selectedId || (await save({})).version_id;

  return {
    versions, selectedId, setSelectedId: id => { setSelectedId(id); setEditing(false); },
    data, layout: activeLayout, loading, sourceChanged, error, editableFields,
    editing, draft, setDraft, beginEdit, cancelEdit: () => setEditing(false), saveDraft,
    ensureVersion, refreshFromLot: () => { setSelectedId(null); setEditing(false); },
  };
}
